import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextProps } from 'af-root/context/fieldReportFullViewFilterContext';
import { withTaskRecovery } from 'af-root/context/taskRecoveryContext';
import { useFieldReportFullViewFilterContext } from 'af-root/hooks/useFieldReportFullViewFilterContext';
import { useTaskRecovery } from 'af-root/hooks/useTaskRecovery';

import type WorkOrderForReportsTableVM from 'ab-viewModels/workOrder/workOrderForReportsTable.viewModel';

import type { FieldReportTableAdditionalFilters } from 'ab-paramModels/workOrder.params';

import { filterMap, getId } from '@acceligentllc/shared/utils/array';
import { isAllowed } from '@acceligentllc/shared/utils/auth';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { FieldReportAccessRole } from '@acceligentllc/shared/enums/fieldReportAccessRole';
import type { CalculatedReportDisplayStatus } from '@acceligentllc/shared/enums/reportDisplayStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';
import { WorkSummaryStatus } from '@acceligentllc/shared/enums/workSummaryStatus';

import { CUSTOMER_SERVICE_NAME } from '@acceligentllc/shared/constants/value';

import type { RootState } from 'af-reducers';

import * as ColorUtils from 'af-utils/color.util';
import { downloadCSV } from 'af-utils/csv.utils';
import { useToggle } from 'af-utils/react.util';
import { phoneNormalizer } from 'af-utils/reduxForms.util';
import * as SettingsUtils from 'af-utils/settings.util';
import { setItem } from 'af-utils/settings.util';

import BrowserStorageEnum from 'ab-enums/browserStorage.enum';
import PagePermissions from 'ab-enums/pagePermissions.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';
import type { UserTypeFilterOption } from 'ab-enums/userTypeFilter.enum';
import UserTypeFilter from 'ab-enums/userTypeFilter.enum';

import CLIENT from 'af-constants/routes/client';
import * as SettingsKeys from 'af-constants/settingsKeys';

import type { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { infoToast } from '@acceligentllc/storybook';

import withFieldReportFullViewFilterContext from 'af-components/FieldReportFullViewFilterContextWrapper';
import LabelWithColor from 'af-components/LabelWithColor';
import LoadingOverlay from 'af-components/LoadingOverlay';
import type { ActionSuccessModalProps } from 'af-components/Modals/ActionSuccessModal';
import ActionSuccessModal from 'af-components/Modals/ActionSuccessModal';
import type { ContactActionModalProps, ContactActionModalSubmitActionCallbackData } from 'af-components/Modals/ContactActionModal';
import ContactActionModal from 'af-components/Modals/ContactActionModal';
import type { TableRef } from 'af-components/Table';
import TableNew from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import TextCell from 'af-components/Table/Cells/TextCell';
import AddFilter from 'af-components/Table/Filters/AddFilter';
import DateFilterNew from 'af-components/Table/Filters/DateFilter';
import MainFilter from 'af-components/Table/Filters/MainFilter';
import SelectToLoad from 'af-components/Table/States/SelectToLoad';
import type { ButtonData, TableProps } from 'af-components/Table/types';
import ComparableDateCell from 'af-components/Table6/Cells/ComparableDateCell';
import DateCell from 'af-components/Table6/Cells/DateCell';
import LinkCell from 'af-components/Table6/Cells/LinkCell';
import ResourceTooltipList from 'af-components/Table6/Cells/ResourceCell/ResourceTooltipList';
import WorkLocationAddressCell from 'af-components/Table6/Cells/WorkLocationAddressCell';
import Tooltip from 'af-components/Tooltip';
import WSStatusLabel from 'af-components/WorkSummaryStatusLabel';

import * as FieldReportActions from 'af-actions/fieldReport';
import * as WorkOrderActions from 'af-actions/workOrder';
import * as WorkRequestActions from 'af-actions/workRequests';

import BulkFilteringModal from './BulkFilteringModal';
import type { CrewTypeFilterOption } from './Filters/CrewTypeFilter';
import FullTableAdditionalFilters from './Filters/FullTableAdditionalFilters';
import type { JobFilterOption } from './Filters/JobFilter';
import MainJobFilter from './Filters/MainJobFilter';
import type { ProjectManagerFilterOption } from './Filters/ProjectManagerFilter';
import type { ReviewStatusFilterOption } from './Filters/ReviewStatusFilter';
import type { SuperintendentFilterOption } from './Filters/SuperintendentFilter';
import type { WorkSummaryStatusFilterOption } from './Filters/WorkSummaryStatusFilter';
import MultipleJobReportsActionModal from './MultipleJobReportsActionModal';
import type { MultipleReportsActionModalPropsWithoutClose, MultipleReportsActionModalSubmitActionCallbackData } from './MultipleReportsActionModal';
import MultipleReportsActionModal from './MultipleReportsActionModal';
import styles from './styles.module.scss';
import VirtualReportExportModal from './VirtualReportExportModal';
import DisplayReviewStatusLabel from '../../Shared/DisplayReviewStatusLabelWithTooltip';

const ALLOWED_RETRIES = 4;

interface SettingProps {
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
	mainFilter: number;
	jobFilterId: number;
	projectManagerFilter: ProjectManagerFilterOption[];
	superintendentFilter: SuperintendentFilterOption[];
	crewTypeFilter: CrewTypeFilterOption[];
	reviewStatusFilter: ReviewStatusFilterOption[];
	workSummaryStatusFilter: WorkSummaryStatusFilterOption[];
	requiresAttentionFilter: boolean;
	excludeCancelledFilter: boolean;
	assignmentFilter: Nullable<UserTypeFilterOption>;
	jobsFilter: JobFilterOption[];
}

const connector = connect(mapStateToProps, mapDispatchToProps());

type Props = SettingProps & ConnectedProps<typeof connector> & FieldReportFullViewFilterContextProps;

type ActionSuccessModalState = Omit<ActionSuccessModalProps, 'closeModal'>;
type ContactActionModalState = Omit<ContactActionModalProps, 'closeModal'>;
type MultipleReportsActionModalState = MultipleReportsActionModalPropsWithoutClose;

const INITIAL_ACTION_SUCCESS_MODAL_STATE: ActionSuccessModalState = {
	showModal: false,
	modalTitle: '',
	descriptionTitle: null,
	descriptionMessage: null,
};

const INITIAL_CONTACT_ACTION_MODAL_STATE: ContactActionModalState = {
	showModal: false,
	title: '',
	description: null,
	action: () => console.warn('Cannot call action for uninitialized modal'), // eslint-disable-line no-console
	actionLabel: 'Continue',
};

const SEND_MULTIPLE_REPORTS_TO_CUSTOMER_DESCRIPTION = (
	<>
		Select time range and Reports you wish to send to the Customer.<br />
		When sending in bulk, Customer will download all reports as a single file.
	</>
);

const INITIAL_MULTIPLE_REPORTS_ACTION_MODAL_STATE: MultipleReportsActionModalState = {
	workRequestId: null,
	workOrderDueDate: null,
	showModal: false,
	title: '',
	jobTitle: '',
	customerCompany: '',
	description: SEND_MULTIPLE_REPORTS_TO_CUSTOMER_DESCRIPTION,
	onSubmit: () => console.warn('Cannot call action for uninitialized modal'), // eslint-disable-line no-console
};

const EXPORT_TO_FILE_LABEL = (
	<>
		<b>Exporting to file.</b><br />
		<br />
		Larger files may take several minutes. Do not close or refresh this tab.<br />
		You can continue using Acceligent in a separate tab while download is in progress.
	</>
);

const SEND_MULTIPLE_REPORTS_CUSTOMER_SIGNATURE_REQUEST_DESCRIPTION = (
	<>
		Select time range and Reports you wish to send to the Customer for signature.
	</>
);

const EXPORT_MULTIPLE_TO_CSV_DESCRIPTION = (
	<>
		Select time range and Reports you wish to download.<br />
		All reports will be downloaded as a single sheet.
	</>
);

const EXPORT_MULTIPLE_COMPLETE_REPORTS_DESCRIPTION = (
	<>
		Select time range and Reports you wish to download.<br />
		All reports will be downloaded as a single file.
	</>
);

export const MAIN_FILTER_OPTION_IDS = {
	DATE: 1,
	JOB: 2,
};

export enum ADDITIONAL_FILTERS {
	SUPERINTENDENT = 'SUPERINTENDENT',
	PROJECT_MANAGER = 'PROJECT_MANAGER',
	REVIEW_STATUS = 'REVIEW_STATUS',
	WORK_SUMMARY_STATUS = 'WORK_SUMMARY_STATUS',
	CREW_TYPE = 'CREW_TYPE',
	JOB = 'JOB',
	REQUIRES_MY_ATTENTION = 'REQUIRES_MY_ATTENTION',
	ASSIGNMENT = 'ASSIGNMENT',
	EXCLUDE_CANCELLED = 'EXCLUDE_CANCELLED',
}

const ADDITIONAL_FILTERS_LABELS = {
	[ADDITIONAL_FILTERS.SUPERINTENDENT]: 'Superintendent',
	[ADDITIONAL_FILTERS.PROJECT_MANAGER]: 'Project Manager',
	[ADDITIONAL_FILTERS.REVIEW_STATUS]: 'Review Status',
	[ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS]: 'Work Summary Status',
	[ADDITIONAL_FILTERS.CREW_TYPE]: 'Crew Type',
	[ADDITIONAL_FILTERS.JOB]: 'Job',
	[ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION]: 'Requires My Attention',
	[ADDITIONAL_FILTERS.ASSIGNMENT]: 'Assignment',
	[ADDITIONAL_FILTERS.EXCLUDE_CANCELLED]: 'Exclude Cancelled Field Reports',
};

export const ADDITIONAL_FILTERS_OPTIONS = Object.values(ADDITIONAL_FILTERS).map((value) => ({
	id: value,
	label: ADDITIONAL_FILTERS_LABELS[value],
}));

export const MAIN_FILTER_OPTIONS = [
	{
		id: MAIN_FILTER_OPTION_IDS.DATE,
		label: 'Date',
	},
	{
		id: MAIN_FILTER_OPTION_IDS.JOB,
		label: 'Job',
	},
];

export interface FieldReportBulkFilters {
	mainFilterId: number;
	dateRange?: { startDate: Date; endDate: Date; };
	period?: TimePeriodRecurrence;
	jobFilter?: Nullable<number>;
	jobFilters: JobFilterOption[];
	projectManagerFilters: ProjectManagerFilterOption[];
	superintendentFilters: SuperintendentFilterOption[];
	assignmentFilter: Nullable<UserTypeFilterOption>;
	crewTypeFilters: CrewTypeFilterOption[];
	reviewStatusFilters: ReviewStatusFilterOption[];
	workSummaryStatusFilters: WorkSummaryStatusFilterOption[];
	requiresMyAttention: boolean;
	searchText: string;
	excludeCancelled: boolean;
}

const ActionSuccessModalFormatters = {
	arraySeparator: (index?: number, array?: string[]) => !!array?.length && ((index ?? 0) < (array.length - 1)) ? ', ' : '',
	emailFormatter: (email: string, index?: number, array?: string[]) =>
		<><b key={email}>{email}</b>{ActionSuccessModalFormatters.arraySeparator(index, array)}</>,
	phoneFormatter: (phone: string, index?: number, array?: string[]) =>
		<><b key={phone}>{phoneNormalizer(phone, '')}</b>{ActionSuccessModalFormatters.arraySeparator(index, array)}</>,
};

const AVAILABLE_DATE_PERIODS = [
	{ id: TimePeriodRecurrence.DAILY, label: 'Daily' },
	{ id: TimePeriodRecurrence.WEEKLY, label: 'Weekly' },
	{ id: TimePeriodRecurrence.MONTHLY, label: 'Monthly' },
	{ id: TimePeriodRecurrence.FISCAL_YEAR, label: 'Fiscal Year' },
	{ id: TimePeriodRecurrence.LAST_6_MONTHS, label: 'Last 6 Months' },
	{ id: TimePeriodRecurrence.YEAR_TO_DATE, label: 'Year to Date' },
];

function setFieldReportMainFilterId(id: number, companyId: string): void {
	setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_MAIN_FILTER(companyId), `${id}`, BrowserStorageEnum.LOCAL_STORAGE);
}

const WorkOrdersReportsListFullTable = (props: Props) => {
	const {
		hasPermissionToViewAll,
		companyData,
		isAccountingOrAdmin,
		isManagement,
		findReportsCSVData,
		findOrCreatePublicReportLink,
		findAllForReportsTable,
		findOrCreateJobReportsBulkGroupCode,
		sendCustomerSignatureRequestForReports,
		downloadJobReportsPdf,
		sendReportsForCustomer,
		findOrCreateCompleteReportBulkGroupCode,
		downloadPublicReportsPdf,
		downloadCompleteReportsPdf,
		findOrCreateCompleteVirtualGroupCode,
		downloadCompleteVirtualReportPDF,
		findOrCreatePublicVirtualReportGroupCode,
		downloadCustomerVirtualReportPDF,
	} = props;

	const navigate = useNavigate();
	const location = useLocation();
	const orgAlias = location.state.orgAlias;

	const isInitialMount = React.useRef(true);

	const PAGE_NUMBER_KEY = React.useMemo(() => SettingsKeys.FIELD_REPORT_FULL_TABLE_PAGE_NUMBER(`${companyData.id}`), [companyData.id]);
	const PAGE_SEARCH_TEXT_KEY = React.useMemo(() => SettingsKeys.FIELD_REPORT_FULL_TABLE_SEARCH_TEXT(`${companyData.id}`), [companyData.id]);
	const SELECTED_ROW_KEY = React.useMemo(() => SettingsKeys.FIELD_REPORT_FULL_TABLE_SELECTED_ROW(`${companyData.id}`), [companyData.id]);

	const initial = React.useMemo(() => {
		const qp = new URLSearchParams(location.search);

		const initialPeriod = (qp.get('period') as TimePeriodRecurrence) || props.period;
		const initialStart = qp.get('start')
			? TimeUtils.normalizeDateToDate(qp.get('start')!, TimeFormat.DATE_ONLY)!
			: props.startDate;

		const initialEnd = qp.get('end')
			? TimeUtils.normalizeDateToDate(qp.get('end')!, TimeFormat.DATE_ONLY)!
			: props.endDate;

		const initialMain = qp.get('main')
			? +qp.get('main')!
			: (props.mainFilter === MAIN_FILTER_OPTION_IDS.JOB ? MAIN_FILTER_OPTION_IDS.JOB : MAIN_FILTER_OPTION_IDS.DATE);

		const initialSearchText = qp.get('q') ?? '';

		const initialJobFilter = qp.get('jobFilter') ? +qp.get('jobFilter')! : props.jobFilterId;

		const assignmentFilterJSON = qp.get('assignmentFilter') ? JSON.parse(qp.get('assignmentFilter')!) : null;
		const initialAssignmentFilter = qp.get('assignmentFilter')
			? { id: assignmentFilterJSON.id, label: assignmentFilterJSON.label } as UserTypeFilterOption
			: props.assignmentFilter ?? null;

		const parseJSON = (key: string) => {
			return (
				qp.get(key)?.split(';').map((s) => JSON.parse(s)) ?? []
			);
		};

		const parsedJobFilters = parseJSON('jobFilters');
		const parsedProjectManagerFilters = parseJSON('projectManagers');
		const parsedSuperintendentFilters = parseJSON('superintendents');
		const parsedCrewTypeFilters = parseJSON('crewTypes');
		const parsedReviewStatusFilters = parseJSON('reviewStatuses');
		const parsedWorkSummaryStatusFilters = parseJSON('workSummaryStatuses');

		const initialJobFilters = parsedJobFilters.length ? parsedJobFilters : props.jobsFilter ?? [];
		const initialProjectManagerFilters = parsedProjectManagerFilters.length ? parsedProjectManagerFilters : props.projectManagerFilter ?? [];
		const initialSuperintendentFilters = parsedSuperintendentFilters.length ? parsedSuperintendentFilters : props.superintendentFilter ?? [];
		const initialCrewTypeFilters = parsedCrewTypeFilters.length ? parsedCrewTypeFilters : props.crewTypeFilter ?? [];
		const initialReviewStatusFilters = parsedReviewStatusFilters.length ? parsedReviewStatusFilters : props.reviewStatusFilter ?? [];
		const initialWorkSummaryStatusFilters = parsedWorkSummaryStatusFilters.length ? parsedWorkSummaryStatusFilters : props.workSummaryStatusFilter ?? [];

		const initialRequiresMyAttention = qp.has('requiresMyAttention') || props.requiresAttentionFilter;
		const initialExcludeCancelled = qp.has('excludeCancelled') || props.excludeCancelledFilter;

		const initialActiveAdditionalFilters: typeof ADDITIONAL_FILTERS_OPTIONS = [];

		if (initialJobFilters.length) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.JOB,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.JOB],
			});
		}
		if (initialProjectManagerFilters.length) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.PROJECT_MANAGER,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.PROJECT_MANAGER],
			});
		}
		if (initialSuperintendentFilters.length) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.SUPERINTENDENT,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.SUPERINTENDENT],
			});
		}
		if (initialCrewTypeFilters.length) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.CREW_TYPE,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.CREW_TYPE],
			});
		}
		if (initialReviewStatusFilters.length) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.REVIEW_STATUS,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.REVIEW_STATUS],
			});
		}
		if (initialWorkSummaryStatusFilters.length) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS],
			});
		}
		if (initialRequiresMyAttention) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION],
			});
		}
		if (initialExcludeCancelled) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.EXCLUDE_CANCELLED,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.EXCLUDE_CANCELLED],
			});
		}
		if (initialAssignmentFilter) {
			initialActiveAdditionalFilters.push({
				id: ADDITIONAL_FILTERS.ASSIGNMENT,
				label: ADDITIONAL_FILTERS_LABELS[ADDITIONAL_FILTERS.ASSIGNMENT],
			});
		}

		return {
			period: initialPeriod,
			startDate: initialStart,
			endDate: initialEnd,
			mainFilterId: initialMain,
			searchText: initialSearchText,
			jobFilter: initialJobFilter,
			assignmentFilter: initialAssignmentFilter,
			jobFilters: initialJobFilters,
			projectManagerFilters: initialProjectManagerFilters,
			superintendentFilters: initialSuperintendentFilters,
			crewTypeFilters: initialCrewTypeFilters,
			reviewStatusFilters: initialReviewStatusFilters,
			workSummaryStatusFilters: initialWorkSummaryStatusFilters,
			requiresMyAttention: initialRequiresMyAttention,
			initialActiveAdditionalFilters,
		};
	}, [
		location.search,
		props.period,
		props.startDate,
		props.endDate,
		props.mainFilter,
		props.jobFilterId,
		props.assignmentFilter,
		props.jobsFilter,
		props.projectManagerFilter,
		props.superintendentFilter,
		props.crewTypeFilter,
		props.reviewStatusFilter,
		props.workSummaryStatusFilter,
		props.requiresAttentionFilter,
		props.excludeCancelledFilter,
	]);

	const [dateRange, setDateRange] = React.useState<{ startDate: Date; endDate: Date; }>({
		startDate: initial.startDate,
		endDate: initial.endDate,
	});
	const { startDate, endDate } = dateRange;
	const [period, setPeriod] = React.useState(initial.period);
	const {
		value: showDownloadingModal,
		setToTrue: openDownloadingModal,
		setToFalse: closeDownloadingModal,
	} = useToggle(false);
	const {
		value: showMultipleJobReportModal,
		setToTrue: openMultipleJobReportModal,
		setToFalse: closeMultipleJobReportModal,
	} = useToggle(false);

	const [jobFilter, setJobFilter] = React.useState<Nullable<number>>(initial.jobFilter);
	const [activeAdditionalFilters, setActiveAdditionalFilters] = React.useState<typeof ADDITIONAL_FILTERS_OPTIONS>(initial.initialActiveAdditionalFilters);

	const [actionSuccessModalState, setActionSuccessModalState] = React.useState(INITIAL_ACTION_SUCCESS_MODAL_STATE);
	const [contactActionModalState, setContactActionModalState] = React.useState(INITIAL_CONTACT_ACTION_MODAL_STATE);
	const [
		multipleReportsActionModalState,
		setMultipleReportsActionModalState,
	] = React.useState<MultipleReportsActionModalState>(INITIAL_MULTIPLE_REPORTS_ACTION_MODAL_STATE);

	const [selectedMainFilterId, setSelectedMainFilterId] = React.useState<number>(initial.mainFilterId);
	const [selectedJobFilters, setSelectedJobFilters] = React.useState<JobFilterOption[]>(initial.jobFilters);
	const [selectedProjectManagerFilters, setSelectedProjectManagerFilters] = React.useState<ProjectManagerFilterOption[]>(initial.projectManagerFilters);
	const [selectedSuperintendentFilters, setSelectedSuperintendentFilters] = React.useState<SuperintendentFilterOption[]>(initial.superintendentFilters);
	const [assignmentFilter, setAssignmentFilter] = React.useState<Nullable<UserTypeFilterOption>>(initial.assignmentFilter);
	const [selectedCrewTypeFilters, setSelectedCrewTypeFilters] = React.useState<CrewTypeFilterOption[]>(initial.crewTypeFilters);
	const [selectedReviewStatusFilters, setSelectedReviewStatusFilters] = React.useState<ReviewStatusFilterOption[]>(initial.reviewStatusFilters);
	const [selectedWorkSummaryStatusFilters, setSelectedWorkSummaryStatusFilters]
		= React.useState<WorkSummaryStatusFilterOption[]>(initial.workSummaryStatusFilters);
	const [autoOpenFilters, setAutoOpenFilters] = React.useState(false);
	const [autoRefresh, setAutoRefresh] = React.useState(true);

	const [showExportCompleteVirtualReportModal, setShowExportCompleteVirtualReportModal] = React.useState(false);
	const [showExportPublicVirtualReportModal, setShowExportPublicVirtualReportModal] = React.useState(false);
	const [exportVirtualReportModalWorkOrder, setExportVirtualReportModalWorkOrder] = React.useState<Nullable<WorkOrderForReportsTableVM>>(null);

	const requiresMyAttentionFilter = React.useMemo(() => {
		return activeAdditionalFilters.some((f) => f.id === ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION);
	}, [activeAdditionalFilters]);

	const excludeCancelledFilter = React.useMemo(() => {
		return activeAdditionalFilters.some((f) => f.id === ADDITIONAL_FILTERS.EXCLUDE_CANCELLED);
	}, [activeAdditionalFilters]);

	const [isBulkFilterModalOpen, setIsBulkFilterModalOpen] = React.useState(false);

	const tableRef = React.useRef<TableRef<WorkOrderForReportsTableVM>>(null);

	const tableTextFilter = tableRef.current?.getFilterText() ?? '';

	const fieldReportFullViewFilterContext = useFieldReportFullViewFilterContext();

	const taskRecovery = useTaskRecovery();

	const resetActionSuccessModalState = React.useCallback(() => {
		setActionSuccessModalState(INITIAL_ACTION_SUCCESS_MODAL_STATE);
	}, []);

	const resetContactActionModalState = React.useCallback(() => {
		setContactActionModalState(INITIAL_CONTACT_ACTION_MODAL_STATE);
	}, []);

	const resetMultipleReportsActionModalState = React.useCallback(() => {
		setMultipleReportsActionModalState(INITIAL_MULTIPLE_REPORTS_ACTION_MODAL_STATE);
	}, []);

	const onSubmitMultipleJobReports = React.useCallback(async (ids: number[], from: Date, to: Date) => {
		closeMultipleJobReportModal();

		infoToast(`${ids.join('|')}-customer`, { title: 'Started preparing job reports for download...' });
		const bulkGroupCode = await findOrCreateJobReportsBulkGroupCode(
			ids,
			TimeUtils.formatDate(from, TimeFormat.DB_DATE_ONLY),
			TimeUtils.formatDate(to, TimeFormat.DB_DATE_ONLY)
		);

		if (!!bulkGroupCode) {
			const fileName = `${TimeUtils.formatDate(from, TimeFormat.DATE_ONLY)}_${TimeUtils.formatDate(to, TimeFormat.DATE_ONLY)}_report_${TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY_SHORT_YEAR)}`;
			await downloadJobReportsPdf(bulkGroupCode, fileName, taskRecovery, ALLOWED_RETRIES);
		}
	}, [closeMultipleJobReportModal, downloadJobReportsPdf, findOrCreateJobReportsBulkGroupCode, taskRecovery]);

	const refreshDataAndPagination = React.useCallback(() => {
		tableRef.current?.resetPagination();
	}, [tableRef]);

	const asyncOpenMultipleJobReportModal = React.useCallback(async () => openMultipleJobReportModal(), [openMultipleJobReportModal]);

	const getRowUrlForOnClick = React.useCallback((original: WorkOrderForReportsTableVM): string => {

		if (original?.id && companyData?.name) {
			return CLIENT.COMPANY.FIELD_REPORT.ALL_REPORTS(original.id.toString(), orgAlias, companyData.name);
		}
		return location.pathname;
	}, [companyData.name, location.pathname, orgAlias]);

	const onRowClick = React.useCallback((row: Row<WorkOrderForReportsTableVM>) => {
		if (row.original?.id) {
			navigate(getRowUrlForOnClick(row.original));
		}
	}, [getRowUrlForOnClick, navigate]);

	const fetchData = React.useCallback(async (tableRequestModel: TableQuery) => {
		if (selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB && !jobFilter) {
			return { rows: [], totalCount: 0, pages: 1 };
		}

		const additionalFilters: FieldReportTableAdditionalFilters = {
			assignmentFilter: assignmentFilter?.id ?? UserTypeFilter.ALL,
			crewTypeFilter: filterMap(
				selectedCrewTypeFilters,
				(option) => typeof option.id === 'number',
				(option) => option.id as number
			),
			jobFilter: selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB ? [jobFilter as number] : selectedJobFilters.map(getId),
			projectManagerFilter: selectedProjectManagerFilters?.map((_projectManager) => _projectManager !== null ? _projectManager.id : null),
			superintendentFilter: selectedSuperintendentFilters.map(getId),
			requiresMyAttention: requiresMyAttentionFilter,
			reviewStatusFilter: selectedReviewStatusFilters.map((s) => s.id as CalculatedReportDisplayStatus),
			workSummaryStatusFilter: selectedWorkSummaryStatusFilters.map((s) => s.id as WorkSummaryStatus),
			internalCrewTypeFilter: filterMap(
				selectedCrewTypeFilters,
				(option) => option.isInternal,
				(option) => option.label
			),
			excludeCancelled: excludeCancelledFilter,
		};

		const _startDate = selectedMainFilterId === MAIN_FILTER_OPTION_IDS.DATE ? startDate : null;
		const _endDate = selectedMainFilterId === MAIN_FILTER_OPTION_IDS.DATE ? endDate : null;
		return await findAllForReportsTable(tableRequestModel, _startDate, _endDate, additionalFilters);
	}, [
		assignmentFilter?.id,
		endDate,
		findAllForReportsTable,
		jobFilter,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedMainFilterId,
		selectedProjectManagerFilters,
		selectedReviewStatusFilters,
		selectedSuperintendentFilters,
		startDate,
		selectedWorkSummaryStatusFilters,
		requiresMyAttentionFilter,
		excludeCancelledFilter,
	]);

	const getReportRowClassName = React.useCallback((rowInfo: Row<WorkOrderForReportsTableVM>) => {
		return rowInfo.original.status === WorkOrderStatus.CANCELED
			? 'field-report-orders-table__row--canceled'
			: '';
	}, []);

	const additionalFilterOptions = React.useMemo(() => {
		const activeAdditionalFiltersIds = activeAdditionalFilters.map((f) => f.id);
		return ADDITIONAL_FILTERS_OPTIONS.filter((option) => {
			if (selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB && option.id === ADDITIONAL_FILTERS.JOB) {
				return false;
			}
			return !activeAdditionalFiltersIds.includes(option.id);
		});
	}, [activeAdditionalFilters, selectedMainFilterId]);

	const contactActionModalDataToActionSuccessDescription = (data: ContactActionModalSubmitActionCallbackData) => {
		const { emails, phones } = data;

		return (
			<>
				{!!emails.length &&
					<>
						the email{emails.length === 1 ? '' : 's'}: {emails.map(ActionSuccessModalFormatters.emailFormatter)}
					</>
				}
				{(!!emails.length && !!phones.length) && <>, and to </>}
				{!!phones.length &&
					<>
						the phone number{phones.length === 1 ? '' : 's'}: {phones.map(ActionSuccessModalFormatters.phoneFormatter)}
					</>
				}
			</>
		);
	};

	const setMultipleReportsActionModalStateVisibility = React.useCallback((showModal: boolean) => {
		setMultipleReportsActionModalState({ ...multipleReportsActionModalState, showModal });
	}, [multipleReportsActionModalState]);

	const exportToCustomerPDF = React.useCallback(async (original: WorkOrderForReportsTableVM) => {
		infoToast(`${original.id}-customer`, { title: 'Started preparing customer report for download...' });
		const publicLink = await findOrCreatePublicReportLink([original.id]);
		if (!!publicLink && companyData?.name) {
			await downloadPublicReportsPdf(publicLink, `${original.code}_report`, taskRecovery, ALLOWED_RETRIES);
		}
	}, [companyData?.name, downloadPublicReportsPdf, findOrCreatePublicReportLink, taskRecovery]);

	const exportToCompletePDF = React.useCallback(async (original: WorkOrderForReportsTableVM) => {
		infoToast(`${original.id}-complete`, { title: 'Started preparing complete report for download...' });
		const bulkGroupCode = await findOrCreateCompleteReportBulkGroupCode([original.id]);
		if (!!bulkGroupCode) {
			const fileName = `${original.code}_report_${TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY_SHORT_YEAR)}`;
			await downloadCompleteReportsPdf(bulkGroupCode, fileName, taskRecovery, ALLOWED_RETRIES);
		}
	}, [downloadCompleteReportsPdf, findOrCreateCompleteReportBulkGroupCode, taskRecovery]);

	const exportToCSV = React.useCallback(async (original: WorkOrderForReportsTableVM) => {
		openDownloadingModal();
		const data = await findReportsCSVData([original.id]);
		const fileName = `${original.code}_report_${TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY_SHORT_YEAR)}.csv`;
		downloadCSV([data!.headerRow, ...data!.dataRows], fileName);
		closeDownloadingModal();
	}, [closeDownloadingModal, findReportsCSVData, openDownloadingModal]);

	const openExportMultipleCompleteReports = React.useCallback((original: WorkOrderForReportsTableVM) => {
		const title = 'Export Complete Reports';
		const { jobCode, jobTitle, customerCompany, workRequestId, dueDate } = original;

		setMultipleReportsActionModalState({
			showModal: true,
			title: title,
			jobTitle: jobTitle,
			customerCompany: customerCompany,
			description: EXPORT_MULTIPLE_COMPLETE_REPORTS_DESCRIPTION,
			workRequestId: workRequestId,
			workOrderDueDate: dueDate,
			onSubmit: async ({ selectedWorkOrderIds }: MultipleReportsActionModalSubmitActionCallbackData) => {
				// NOTE: maintain SendMultipleReportsModalState for back action
				setMultipleReportsActionModalStateVisibility(false);
				infoToast(`${selectedWorkOrderIds.join('|')}-complete`, { title: 'Started preparing bulk reports for download...' });
				const bulkGroupCode = await findOrCreateCompleteReportBulkGroupCode(selectedWorkOrderIds);

				if (!!bulkGroupCode) {
					const fileName = `${jobCode}_report_${TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY_SHORT_YEAR)}`;
					await downloadCompleteReportsPdf(bulkGroupCode, fileName, taskRecovery, ALLOWED_RETRIES);
				}
				resetMultipleReportsActionModalState();
			},
		});
	}, [
		setMultipleReportsActionModalStateVisibility,
		findOrCreateCompleteReportBulkGroupCode,
		resetMultipleReportsActionModalState,
		downloadCompleteReportsPdf,
		taskRecovery,
	]);

	const openExportMultipleToCSV = React.useCallback((original: WorkOrderForReportsTableVM) => {
		const title = 'Export Reports to CSV';
		const { jobCode, jobTitle, customerCompany, workRequestId, dueDate } = original;

		setMultipleReportsActionModalState({
			showModal: true,
			title: title,
			jobTitle: jobTitle,
			customerCompany: customerCompany,
			description: EXPORT_MULTIPLE_TO_CSV_DESCRIPTION,
			workRequestId: workRequestId,
			workOrderDueDate: dueDate,
			onSubmit: async ({ selectedWorkOrderIds }: MultipleReportsActionModalSubmitActionCallbackData) => {
				// NOTE: maintain SendMultipleReportsModalState for back action
				setMultipleReportsActionModalStateVisibility(false);
				openDownloadingModal();

				const data = await findReportsCSVData(selectedWorkOrderIds);
				const fileName = `${jobCode}_report_${TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY_SHORT_YEAR)}.csv`;
				downloadCSV([data!.headerRow, ...data!.dataRows], fileName);
				resetMultipleReportsActionModalState();
				closeDownloadingModal();
			},
		});
	}, [closeDownloadingModal, findReportsCSVData, openDownloadingModal, resetMultipleReportsActionModalState, setMultipleReportsActionModalStateVisibility]);

	const openSendReportCustomerSignatureRequestModal = React.useCallback((original: WorkOrderForReportsTableVM) => {
		const title = 'Send Report and Request Customer Signature';
		const { code, id } = original;
		setContactActionModalState({
			showModal: true,
			title: title,
			description: (<span>You are about to send Report <b>{code}</b> for Customer to sign.</span>),
			action: async (data: ContactActionModalSubmitActionCallbackData, formName: string) => {
				await sendCustomerSignatureRequestForReports({ ...data, workOrderIds: [id] }, formName);
				resetContactActionModalState();
				setActionSuccessModalState({
					showModal: true,
					modalTitle: title,
					descriptionTitle: (<span>Signature Requested</span>),
					descriptionMessage: (
						<span>
							<>Report {code} has been successfully sent to </>
							{contactActionModalDataToActionSuccessDescription(data)}
							<>, and Customer signature has been requested.</>
						</span>
					),
				});
			},
			actionLabel: 'Send and Request Signature',
		});
	}, [resetContactActionModalState, sendCustomerSignatureRequestForReports]);

	const openSendMultipleReportsCustomerSignatureRequestModal = React.useCallback((original: WorkOrderForReportsTableVM) => {
		const title = 'Send and Request Signature';
		const { jobCode, jobTitle, customerCompany, workRequestId, dueDate } = original;
		setMultipleReportsActionModalState({
			showModal: true,
			title: title,
			jobTitle: jobTitle,
			customerCompany: customerCompany,
			workRequestId: workRequestId,
			workOrderDueDate: dueDate,
			description: SEND_MULTIPLE_REPORTS_CUSTOMER_SIGNATURE_REQUEST_DESCRIPTION,
			onSubmit: ({ selectedWorkOrderIds }: MultipleReportsActionModalSubmitActionCallbackData) => {
				// NOTE: maintain SendMultipleReportsModalState for back action
				setMultipleReportsActionModalStateVisibility(false);
				setContactActionModalState({
					showModal: true,
					title: title,
					description: (
						<span>You are about to send <b>{selectedWorkOrderIds.length}</b> Reports for <b>{jobCode}</b> to Customer.</span>
					),
					action: async (data: ContactActionModalSubmitActionCallbackData, formName: string) => {
						await sendCustomerSignatureRequestForReports({ ...data, workOrderIds: selectedWorkOrderIds, jobCode }, formName);
						resetMultipleReportsActionModalState();
						resetContactActionModalState();
						setActionSuccessModalState({
							showModal: true,
							modalTitle: title,
							descriptionTitle: (<span>Signature Requested</span>),
							descriptionMessage: (
								<span>
									<><b>{selectedWorkOrderIds.length}</b> Reports for {jobCode} has been successfully sent to </>
									{contactActionModalDataToActionSuccessDescription(data)}
									<>, and Customer signature has been requested.</>
								</span>
							),
						});
					},
					goBack: () => {
						resetContactActionModalState();
						setMultipleReportsActionModalStateVisibility(true);
					},
					actionLabel: 'Send',
				});
			},
		});
	}, [
		resetContactActionModalState,
		resetMultipleReportsActionModalState,
		sendCustomerSignatureRequestForReports,
		setMultipleReportsActionModalStateVisibility,
	]);

	const openSendReportToCustomerModal = React.useCallback((original: WorkOrderForReportsTableVM) => {
		const title = 'Send Report to Customer';
		const { code, id } = original;

		setContactActionModalState({
			showModal: true,
			title: title,
			description: (<span>You are about to send Report <b>{code}</b> to Customer.</span>),
			action: async (data: ContactActionModalSubmitActionCallbackData, formName: string) => {
				await sendReportsForCustomer({ ...data, workOrderIds: [id] }, formName);
				resetContactActionModalState();
				setActionSuccessModalState({
					showModal: true,
					modalTitle: title,
					descriptionTitle: (<span>Report Sent</span>),
					descriptionMessage: (
						<span>
							<>Report {code} has been successfully sent to </>
							{contactActionModalDataToActionSuccessDescription(data)}
							<>.</>
						</span>
					),
				});
			},
			actionLabel: 'Send',
		});
	}, [resetContactActionModalState, sendReportsForCustomer]);

	const openSendMultipleReportsToCustomer = React.useCallback((original: WorkOrderForReportsTableVM) => {
		const title = 'Send Reports to Customer';
		const { jobCode, jobTitle, customerCompany, workRequestId, dueDate } = original;

		setMultipleReportsActionModalState({
			showModal: true,
			title: title,
			jobTitle: jobTitle,
			customerCompany: customerCompany,
			description: SEND_MULTIPLE_REPORTS_TO_CUSTOMER_DESCRIPTION,
			workRequestId: workRequestId,
			workOrderDueDate: dueDate,
			onSubmit: ({ selectedWorkOrderIds }: MultipleReportsActionModalSubmitActionCallbackData) => {
				// NOTE: maintain SendMultipleReportsModalState for back action
				setMultipleReportsActionModalStateVisibility(false);
				setContactActionModalState({
					showModal: true,
					title: title,
					description: (
						<span>You are about to send <b>{selectedWorkOrderIds.length}</b> Reports for <b>{jobCode}</b> to Customer.</span>
					),
					action: async (data: ContactActionModalSubmitActionCallbackData, formName: string) => {
						await sendReportsForCustomer({ ...data, workOrderIds: selectedWorkOrderIds }, formName);
						resetMultipleReportsActionModalState();
						resetContactActionModalState();
						setActionSuccessModalState({
							showModal: true,
							modalTitle: title,
							descriptionTitle: (<span>Reports Sent</span>),
							descriptionMessage: (
								<span>
									<><b>{selectedWorkOrderIds.length}</b> Reports for {jobCode} has been successfully sent to </>
									{contactActionModalDataToActionSuccessDescription(data)}
									<>.</>
								</span>
							),
						});
					},
					goBack: () => {
						resetContactActionModalState();
						setMultipleReportsActionModalStateVisibility(true);
					},
					actionLabel: 'Send',
				});
			},
		});
	}, [resetContactActionModalState, resetMultipleReportsActionModalState, sendReportsForCustomer, setMultipleReportsActionModalStateVisibility]);

	const goToSignLocally = React.useCallback(async (original: WorkOrderForReportsTableVM) => {

		const publicLink = await findOrCreatePublicReportLink([original.id]);

		if (!publicLink) {
			throw new Error('Public link not provided');
		}
		window.open(CLIENT.COMPANY.FIELD_REPORT.PUBLIC_REPORT(publicLink, orgAlias, companyData?.name), '_blank')?.focus();
	}, [companyData?.name, findOrCreatePublicReportLink, orgAlias]);

	const hideExportToCSV = React.useCallback((original: WorkOrderForReportsTableVM) => {
		return !props.isAccountingOrAdmin || original.status === WorkOrderStatus.CANCELED;
	}, [props.isAccountingOrAdmin]);

	const openExportCompleteVirtualReportModal = React.useCallback(async (original: WorkOrderForReportsTableVM) => {
		setShowExportCompleteVirtualReportModal(true);
		setExportVirtualReportModalWorkOrder(original);
	}, []);

	const closeCompleteVirtualReportExportModal = React.useCallback(() => {
		setShowExportCompleteVirtualReportModal(false);
		setExportVirtualReportModalWorkOrder(null);
	}, []);

	const openExportPublicVirtualReportModal = React.useCallback(async (original: WorkOrderForReportsTableVM) => {
		setShowExportPublicVirtualReportModal(true);
		setExportVirtualReportModalWorkOrder(original);
	}, []);

	const closePublicVirtualReportExportModal = React.useCallback(() => {
		setShowExportPublicVirtualReportModal(false);
		setExportVirtualReportModalWorkOrder(null);
	}, []);

	const exportCompleteVirtualReportPDF = React.useCallback(async (workRequestId: number, workRequestCode: string) => {
		if (!exportVirtualReportModalWorkOrder) {
			return;
		}

		infoToast(`${workRequestId}-virtual`, { title: 'Started preparing job reports for download...' });

		const publicLink = await findOrCreateCompleteVirtualGroupCode(exportVirtualReportModalWorkOrder.id, workRequestId);
		if (!!publicLink) {
			await downloadCompleteVirtualReportPDF(publicLink, workRequestId, `${exportVirtualReportModalWorkOrder.code}_VR_${workRequestCode}`, taskRecovery, ALLOWED_RETRIES);
		}
	}, [downloadCompleteVirtualReportPDF, exportVirtualReportModalWorkOrder, findOrCreateCompleteVirtualGroupCode, taskRecovery]);

	const exportPublicVirtualReportPDF = React.useCallback(async (workRequestId: number, workRequestCode: string) => {
		if (!exportVirtualReportModalWorkOrder) {
			return;
		}

		infoToast(`${workRequestId}-virtual`, { title: 'Started preparing job reports for download...' });

		const publicLink = await findOrCreatePublicVirtualReportGroupCode(exportVirtualReportModalWorkOrder.id, workRequestId);
		if (!!publicLink) {
			await downloadCustomerVirtualReportPDF(publicLink, workRequestId, `${exportVirtualReportModalWorkOrder.code}_VR_${workRequestCode}`, taskRecovery, ALLOWED_RETRIES);
		}
	}, [downloadCustomerVirtualReportPDF, exportVirtualReportModalWorkOrder, findOrCreatePublicVirtualReportGroupCode, taskRecovery]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<WorkOrderForReportsTableVM, unknown>) => {

		if (_cell.row.original.status === WorkOrderStatus.CANCELED) {
			return null;
		}

		const isPartOfProject = _cell.row.original.isPartOfProject;

		const exportOptions = [
			{
				label: 'Export for Customer',
				onClick: () => exportToCustomerPDF(_cell.row.original),
			},
			{
				label: 'Export Complete Report',
				onClick: () => exportToCompletePDF(_cell.row.original),
			},
			{
				label: 'Export Bulk Complete Report',
				onClick: () => openExportMultipleCompleteReports(_cell.row.original),
			},
		];

		if (isPartOfProject) {
			exportOptions.push({
				label: 'Export Complete Associated Subjob Data',
				onClick: () => openExportCompleteVirtualReportModal(_cell.row.original),
			});
			exportOptions.push({
				label: 'Export Associated Subjob Data for Customer',
				onClick: () => openExportPublicVirtualReportModal(_cell.row.original),
			});
		}

		const actions = [
			{
				label: 'Export to PDF',
				options: exportOptions,
			},
			{
				label: 'Export to CSV',
				disabled: hideExportToCSV(_cell.row.original),
				options: [
					{
						label: 'Export Report Data',
						onClick: () => exportToCSV(_cell.row.original),
					},
					{
						label: 'Export Bulk Report Data',
						onClick: () => openExportMultipleToCSV(_cell.row.original),
					},
				],
			},
			{
				label: 'Send to Customer',
				options: [
					{
						label: `Send Signature Request for ${_cell.row.original.code}`,
						onClick: () => openSendReportCustomerSignatureRequestModal(_cell.row.original),
					},
					{
						label: `Send signature request for reports of ${_cell.row.original.jobCode}`,
						onClick: () => openSendMultipleReportsCustomerSignatureRequestModal(_cell.row.original),
					},
					{ isDivider: true },
					{
						label: `Send ${_cell.row.original.code} Report`,
						onClick: () => openSendReportToCustomerModal(_cell.row.original),
					},
					{
						label: `Send Reports for ${_cell.row.original.jobCode}`,
						onClick: () => openSendMultipleReportsToCustomer(_cell.row.original),
					},
				],
			},
			{
				label: 'Add Customer Signature Locally',
				onClick: () => goToSignLocally(_cell.row.original),
			},
		];

		return (
			<ActionsCell
				fixed={true}
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={actions}
				valueKey="label"
			/>
		);
	}, [
		exportToCSV,
		exportToCompletePDF,
		exportToCustomerPDF,
		hideExportToCSV,
		openExportMultipleCompleteReports,
		openExportMultipleToCSV,
		openSendMultipleReportsCustomerSignatureRequestModal,
		openSendMultipleReportsToCustomer,
		openSendReportCustomerSignatureRequestModal,
		openSendReportToCustomerModal,
		goToSignLocally,
		openExportCompleteVirtualReportModal,
		openExportPublicVirtualReportModal,
	]);

	const changePeriod = React.useCallback((newPeriod: TimePeriodRecurrence, selected: Date) => {
		let from: Date = selected, to: Date = selected;

		switch (newPeriod) {
			case TimePeriodRecurrence.MONTHLY:
				from = TimeUtils.positionDate(from, 'start', 'month');
				to = TimeUtils.positionDate(to, 'end', 'month');
				break;
			case TimePeriodRecurrence.WEEKLY:
				from = TimeUtils.positionDate(from, 'start', 'week');
				to = TimeUtils.positionDate(to, 'end', 'week');
				break;
			case TimePeriodRecurrence.YEAR_TO_DATE:
				from = TimeUtils.positionDate(new Date(), 'start', 'year');
				to = new Date();
				break;
			case TimePeriodRecurrence.LAST_6_MONTHS:
				const _endDate = new Date();
				const _startDate = new Date();
				const day = _endDate.getDate();
				let month = _startDate.getMonth() - 6;
				let year = _startDate.getFullYear();

				if (month < 0) {
					month += 12;
					year -= 1;
				}

				const lastDayOfTarget = new Date(year, month + 1, 0).getDate(); // if we put 0 as date argument, we get the last day index of previous month
				const newDay = Math.min(day, lastDayOfTarget);

				_startDate.setFullYear(year, month, newDay);

				from = _startDate;
				to = _endDate;
				break;
			case TimePeriodRecurrence.FISCAL_YEAR:
				const fiscalMonth = companyData.fiscalYearStartMonth!;
				const selMonth = selected.getMonth() + 1;
				let fyYear = selected.getFullYear();

				if (selMonth < fiscalMonth) {
					fyYear -= 1;
				}

				const fyStart = new Date(fyYear, fiscalMonth, 0);
				const fyEnd = new Date(fyYear + 1, fiscalMonth - 1, 0);
				from = TimeUtils.positionDate(fyStart, 'start', 'month');
				to = TimeUtils.positionDate(fyEnd, 'end', 'day');
				break;
			case TimePeriodRecurrence.DAILY:
			case TimePeriodRecurrence.CUSTOM:

			default:
				break;
		}

		setPeriod(newPeriod);
		setDateRange({ startDate: from, endDate: to });
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_PERIOD(`${companyData.id}`), newPeriod);
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_START_DATE(`${companyData.id}`), TimeUtils.formatDate(from));
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_END_DATE(`${companyData.id}`), TimeUtils.formatDate(to));
	}, [companyData.fiscalYearStartMonth, companyData.id]);

	// refresh hook for additional filters
	React.useEffect(() => {
		// We want to avoid it on initial mount since it refreshes cached pagination
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		if (autoRefresh) {
			refreshDataAndPagination();
		}
		// Don't want refresh and autoRefresh here
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		assignmentFilter,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedProjectManagerFilters,
		selectedReviewStatusFilters,
		selectedSuperintendentFilters,
		jobFilter,
		selectedMainFilterId,
		requiresMyAttentionFilter,
		excludeCancelledFilter,
		dateRange,
		selectedWorkSummaryStatusFilters,
	]);

	const filterByDate = React.useCallback((from: Date, to: Date) => {
		setDateRange({ startDate: from, endDate: to });
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_START_DATE(`${companyData.id}`), TimeUtils.formatDate(from));
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_END_DATE(`${companyData.id}`), TimeUtils.formatDate(to));
	}, [companyData.id]);

	const changeJobFilter = React.useCallback((id: number) => {
		setJobFilter(id);
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_JOB_ID(`${companyData.id}`), `${id}`);
	}, [companyData.id]);

	const addAdditionalFilter = React.useCallback((option: typeof ADDITIONAL_FILTERS_OPTIONS[0]) => {
		setAutoOpenFilters(true);
		setActiveAdditionalFilters([...activeAdditionalFilters, option]);
	}, [activeAdditionalFilters]);

	const changeJobFilters = React.useCallback((jobOptions: JobFilterOption[]) => {
		setSelectedJobFilters(jobOptions);
	}, []);

	const changeProjectManagerFilters = React.useCallback((projectManagerOptions: ProjectManagerFilterOption[]) => {
		if (selectedProjectManagerFilters === null && projectManagerOptions === null) {
			setSelectedProjectManagerFilters([]);
		} else {
			setSelectedProjectManagerFilters(projectManagerOptions);
		}

	}, [selectedProjectManagerFilters]);

	const changeSuperintendentFilters = React.useCallback((superintendentOptions: SuperintendentFilterOption[]) => {
		setSelectedSuperintendentFilters(superintendentOptions);
	}, []);

	const changeAssignmentsFilter = React.useCallback((filter: UserTypeFilterOption) => {
		setAssignmentFilter(filter);
	}, [setAssignmentFilter]);

	const changeCrewTypeFilters = React.useCallback((crewTypeOptions: CrewTypeFilterOption[]) => {
		setSelectedCrewTypeFilters(crewTypeOptions);
	}, []);

	const changeReviewStatusFilters = React.useCallback((statusOptions: ReviewStatusFilterOption[]) => {
		setSelectedReviewStatusFilters(statusOptions);
	}, []);

	const changeWorkSummaryStatusFilters = React.useCallback((statusOptions: WorkSummaryStatusFilterOption[]) => {
		setSelectedWorkSummaryStatusFilters(statusOptions);
	}, []);

	const clearSuperintendentFilters = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.SUPERINTENDENT)]);
		if (selectedSuperintendentFilters.length) {
			setSelectedSuperintendentFilters([]);
		}
	}, [activeAdditionalFilters, selectedSuperintendentFilters.length]);

	const clearProjectManagerFilters = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.PROJECT_MANAGER)]);
		if (selectedProjectManagerFilters?.length || selectedProjectManagerFilters === null) {
			setSelectedProjectManagerFilters([]);
		}
	}, [activeAdditionalFilters, selectedProjectManagerFilters]);

	const clearJobFilters = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.JOB)]);
		if (selectedJobFilters.length) {
			setSelectedJobFilters([]);
		}
	}, [activeAdditionalFilters, selectedJobFilters.length]);

	const clearCrewTypeFilters = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.CREW_TYPE)]);
		if (selectedCrewTypeFilters.length) {
			setSelectedCrewTypeFilters([]);
		}
	}, [activeAdditionalFilters, selectedCrewTypeFilters.length]);

	const clearReviewStatusFilters = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.REVIEW_STATUS)]);
		if (selectedReviewStatusFilters.length) {
			setSelectedReviewStatusFilters([]);
		}
	}, [activeAdditionalFilters, selectedReviewStatusFilters.length]);

	const clearWorkSummaryStatusFilters = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS)]);
		if (selectedWorkSummaryStatusFilters.length) {
			setSelectedWorkSummaryStatusFilters([]);
		}
	}, [activeAdditionalFilters, selectedWorkSummaryStatusFilters.length]);

	const clearAssignmentsFilter = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.ASSIGNMENT)]);
		if (assignmentFilter) {
			setAssignmentFilter(null);
		}
	}, [activeAdditionalFilters, assignmentFilter]);

	const clearRequiresMyAttentionFilter = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION)]);
	}, [activeAdditionalFilters]);

	const clearExcludeCancelledFilter = React.useCallback(() => {
		setActiveAdditionalFilters([...activeAdditionalFilters.filter((f) => f.id !== ADDITIONAL_FILTERS.EXCLUDE_CANCELLED)]);
	}, [activeAdditionalFilters]);

	const changeSelectedMainFilter = React.useCallback((id: number) => {
		setSelectedMainFilterId(id);
		setFieldReportMainFilterId(id, `${companyData.id}`);

		if (id === MAIN_FILTER_OPTION_IDS.DATE) {
			setJobFilter(null);
			SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_JOB_ID(`${companyData.id}`), '');
		} else if (activeAdditionalFilters.some((filter) => filter.id === ADDITIONAL_FILTERS.JOB)) {
			clearJobFilters();
		}
	}, [activeAdditionalFilters, clearJobFilters, companyData.id]);

	const resetAdditionalFilters = React.useCallback(() => {
		setActiveAdditionalFilters([]);
		if (selectedJobFilters.length) {
			setSelectedJobFilters([]);
		}
		if (selectedProjectManagerFilters?.length || selectedProjectManagerFilters === null) {
			setSelectedProjectManagerFilters([]);
		}
		if (selectedSuperintendentFilters.length) {
			setSelectedSuperintendentFilters([]);
		}
		if (selectedCrewTypeFilters.length) {
			setSelectedCrewTypeFilters([]);
		}
		if (selectedReviewStatusFilters.length) {
			setSelectedReviewStatusFilters([]);
		}
		if (selectedWorkSummaryStatusFilters.length) {
			setSelectedWorkSummaryStatusFilters([]);
		}
		if (assignmentFilter) {
			setAssignmentFilter(null);
		}
	}, [
		assignmentFilter,
		selectedCrewTypeFilters.length,
		selectedJobFilters.length,
		selectedProjectManagerFilters,
		selectedReviewStatusFilters.length,
		selectedSuperintendentFilters.length,
		selectedWorkSummaryStatusFilters.length,
	]);

	const datePeriodOptions = React.useMemo(() => {
		if (!!companyData.fiscalYearStartMonth) {
			return AVAILABLE_DATE_PERIODS;
		}
		return AVAILABLE_DATE_PERIODS.filter(({ id }) => id !== TimePeriodRecurrence.FISCAL_YEAR);
	}, [companyData.fiscalYearStartMonth]);

	const renderFilter = React.useCallback(() => {
		return (
			<div className={styles['filter-container']}>
				<MainFilter
					defaultValue={selectedMainFilterId}
					onChange={changeSelectedMainFilter}
					options={MAIN_FILTER_OPTIONS}
				/>
				{
					selectedMainFilterId === MAIN_FILTER_OPTION_IDS.DATE &&
					<DateFilterNew
						changePeriod={changePeriod}
						endDate={endDate}
						onChange={filterByDate}
						options={datePeriodOptions}
						period={period}
						startDate={startDate}
					/>
				}
				{
					selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB &&
					<MainJobFilter
						defaultValue={jobFilter}
						fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
						onChange={changeJobFilter}
					/>
				}
				<AddFilter
					addAdditionalFilter={addAdditionalFilter}
					options={additionalFilterOptions}
				/>
			</div>
		);
	}, [
		addAdditionalFilter,
		additionalFilterOptions,
		changeJobFilter,
		changePeriod,
		changeSelectedMainFilter,
		datePeriodOptions,
		endDate,
		fieldReportFullViewFilterContext,
		filterByDate,
		jobFilter,
		period,
		selectedMainFilterId,
		startDate,
	]);

	interface WorkSummaryReviewStatusInfo {
		fieldReportId: number;
		reviewStatus: Nullable<WorkSummaryStatus>;
		lastUpdatedAt: Nullable<string>;
		lastUpdatedBy: Nullable<string>;
	}
	const renderWorkSummaryStatusCell = React.useCallback(({ cell }: CellContext<WorkOrderForReportsTableVM, WorkSummaryReviewStatusInfo[]>) => {

		const statuses = cell.row.original.workSummaryReviewStatuses;

		return (
			<div className={styles['job-work-summary__review-status-cell']}>
				{statuses?.map((statusObj, idx) => {
					const status = statusObj.reviewStatus ?? WorkSummaryStatus.DRAFT;
					const lastUpdatedAt = statusObj.lastUpdatedAt;
					const lastUpdatedBy = statusObj.lastUpdatedBy?.trim() ? statusObj.lastUpdatedBy : CUSTOMER_SERVICE_NAME;

					const message =
						lastUpdatedBy && lastUpdatedAt
							? <>Work Summary Status <br />{`${lastUpdatedAt} By ${lastUpdatedBy}`}</>
							: null;

					const line = (
						<WSStatusLabel key={idx} status={status as WorkSummaryStatus} />
					);

					return message ? (
						<Tooltip key={idx} message={message} placement="top">
							{line}
						</Tooltip>
					) : (
						line
					);
				})}
			</div>
		);
	},
		[]
	);

	const renderSpecializedFilters = React.useCallback(() => {
		return (
			<FullTableAdditionalFilters
				activeAdditionalFilters={activeAdditionalFilters}
				assignmentFilter={assignmentFilter}
				autoOpenFilters={autoOpenFilters}
				changeAssignmentsFilter={changeAssignmentsFilter}
				changeCrewTypeFilters={changeCrewTypeFilters}
				changeJobFilters={changeJobFilters}
				changeProjectManagerFilters={changeProjectManagerFilters}
				changeReviewStatusFilters={changeReviewStatusFilters}
				changeSuperintendentFilters={changeSuperintendentFilters}
				changeWorkSummaryStatusFilters={changeWorkSummaryStatusFilters}
				clearAssignmentsFilter={clearAssignmentsFilter}
				clearCrewTypeFilters={clearCrewTypeFilters}
				clearExcludeCancelled={clearExcludeCancelledFilter}
				clearJobFilters={clearJobFilters}
				clearProjectManagerFilters={clearProjectManagerFilters}
				clearRequiresMyAttentionFilter={clearRequiresMyAttentionFilter}
				clearReviewStatusFilters={clearReviewStatusFilters}
				clearSuperintendentFilters={clearSuperintendentFilters}
				clearWorkSummaryStatusFilters={clearWorkSummaryStatusFilters}
				fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
				resetAdditionalFilters={resetAdditionalFilters}
				selectedCrewTypeFilters={selectedCrewTypeFilters}
				selectedJobFilters={selectedJobFilters}
				selectedMainFilterId={selectedMainFilterId}
				selectedProjectManagerFilters={selectedProjectManagerFilters}
				selectedReviewStatusFilters={selectedReviewStatusFilters}
				selectedSuperintendentFilters={selectedSuperintendentFilters}
				selectedWorkSummaryStatusFilters={selectedWorkSummaryStatusFilters}
			/>
		);
	}, [
		changeAssignmentsFilter,
		changeCrewTypeFilters,
		changeJobFilters,
		changeProjectManagerFilters,
		changeReviewStatusFilters,
		changeSuperintendentFilters,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedMainFilterId,
		selectedProjectManagerFilters,
		selectedReviewStatusFilters,
		selectedSuperintendentFilters,
		clearAssignmentsFilter,
		clearCrewTypeFilters,
		clearJobFilters,
		clearProjectManagerFilters,
		clearReviewStatusFilters,
		clearSuperintendentFilters,
		clearRequiresMyAttentionFilter,
		autoOpenFilters,
		changeWorkSummaryStatusFilters,
		clearWorkSummaryStatusFilters,
		selectedWorkSummaryStatusFilters,
		fieldReportFullViewFilterContext,
		assignmentFilter,
		activeAdditionalFilters,
		resetAdditionalFilters,
		clearExcludeCancelledFilter,
	]);

	const openBulkFilteringModal = React.useCallback(() => {
		setIsBulkFilterModalOpen(true);
	}, []);

	const closeBulkFilteringModal = React.useCallback(() => {
		setIsBulkFilterModalOpen(false);
	}, []);

	const applyBulkFilters = React.useCallback((filters: FieldReportBulkFilters) => {
		setAutoOpenFilters(false);
		const currentTextFilter = tableRef.current?.getFilterText();
		const shouldChangeTextFilter = filters.searchText !== currentTextFilter;
		if (shouldChangeTextFilter) {
			setAutoRefresh(false);
		}
		const newActiveAdditionalFilters: typeof activeAdditionalFilters = [];
		if (filters.superintendentFilters.length) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.SUPERINTENDENT, label: ADDITIONAL_FILTERS_LABELS.SUPERINTENDENT });
		}
		if (filters.projectManagerFilters === null || filters.projectManagerFilters.length) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.PROJECT_MANAGER, label: ADDITIONAL_FILTERS_LABELS.PROJECT_MANAGER });
		}
		if (filters.reviewStatusFilters.length) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.REVIEW_STATUS, label: ADDITIONAL_FILTERS_LABELS.REVIEW_STATUS });
		}
		if (filters.crewTypeFilters.length) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.CREW_TYPE, label: ADDITIONAL_FILTERS_LABELS.CREW_TYPE });
		}
		if (filters.jobFilters.length) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.JOB, label: ADDITIONAL_FILTERS_LABELS.JOB });
		}
		if (filters.requiresMyAttention) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION, label: ADDITIONAL_FILTERS_LABELS.REQUIRES_MY_ATTENTION });
		}
		if (filters.excludeCancelled) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.EXCLUDE_CANCELLED, label: ADDITIONAL_FILTERS_LABELS.EXCLUDE_CANCELLED });
		}
		if (filters.workSummaryStatusFilters.length) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS, label: ADDITIONAL_FILTERS_LABELS.WORK_SUMMARY_STATUS });
		}
		if (filters.assignmentFilter) {
			newActiveAdditionalFilters.push({ id: ADDITIONAL_FILTERS.ASSIGNMENT, label: ADDITIONAL_FILTERS_LABELS.ASSIGNMENT });
		}
		setActiveAdditionalFilters(newActiveAdditionalFilters);

		setSelectedMainFilterId(filters.mainFilterId);
		setFieldReportMainFilterId(filters.mainFilterId, `${companyData.id}`);
		if (filters.dateRange) {
			setDateRange(filters.dateRange);
		}
		if (filters.period) {
			setPeriod(filters.period);
		}
		setSelectedJobFilters(filters.jobFilters);
		setSelectedProjectManagerFilters(filters.projectManagerFilters);
		setSelectedSuperintendentFilters(filters.superintendentFilters);
		setAssignmentFilter(filters.assignmentFilter);
		setSelectedCrewTypeFilters(filters.crewTypeFilters);
		setSelectedReviewStatusFilters(filters.reviewStatusFilters);
		setSelectedWorkSummaryStatusFilters(filters.workSummaryStatusFilters);
		setJobFilter(filters.jobFilter ?? null);
		if (shouldChangeTextFilter) {
			tableRef.current?.changeFilterText(filters.searchText);
		}

		// Wait for filters to render, and enable auto opening again

		setTimeout(() => {
			setAutoOpenFilters(true);
			if (shouldChangeTextFilter) {
				setAutoRefresh(true);
			}
		}, 1000);
	}, [companyData.id]);

	const columns: TableProps<WorkOrderForReportsTableVM>['columns'] = React.useMemo(() => [
		{
			id: 'workOrderCode',
			header: 'Work Order',
			accessor: 'workOrderCode',
			enableSorting: true,
			enableHiding: true,
			size: 150, // Give some additional space for name of WO, very important
			cell: ({ cell }) => (
				cell.row.original
					? (
						<Tooltip
							className="field-report-orders-table__popover"
							message={
								<ResourceTooltipList
									workOrderEmployees={cell.row.original.workOrderEmployees}
									workOrderEquipment={cell.row.original.workOrderEquipment}
									workOrderTemporaryEmployees={cell.row.original.workOrderTemporaryEmployees}
								/>
							}
						>
							<LinkCell
								label={(
									<div>
										<div className={styles['cell-ellipsed']}>{cell.row.original.code}</div>
										{cell.row.original.status === WorkOrderStatus.CANCELED && <div className="field-report-orders-table__canceled-code">Canceled</div>}
									</div>
								)}
								url={getRowUrlForOnClick(cell.row.original)}
							/>
						</Tooltip>
					)
					: <EmptyCell message="N/A" />
			),
		},
		{
			id: 'calculatedReportDisplayStatus',
			header: 'Review Status',
			accessor: 'calculatedReportDisplayStatus',
			enableSorting: true,
			enableHiding: true,
			size: 136, // 120 for label not to be cut off + padding
			cell: ({ cell }) => {

				if (cell.row.original.status === WorkOrderStatus.CANCELED) {
					return <EmptyCell message="N/A" />;
				}

				let role = cell.row.original.fieldReportAccessRole;
				if (isManagement) {
					role = FieldReportAccessRole.MANAGEMENT;
				}
				if (isAccountingOrAdmin) {
					role = FieldReportAccessRole.ACCOUNTING;
				}

				return (
					<DisplayReviewStatusLabel
						accountId={companyData?.accountId ?? null}
						assignedTo={cell.row.original.assignedTo}
						calculatedReportDisplayStatus={cell.row.original.calculatedReportDisplayStatus}
						fieldReportAccessRole={role}
						isCondensedView={false}
						lastUpdatedAt={cell.row.original.lastUpdatedAt}
						reviewLevel={cell.row.original.reviewLevel}
						reviewStatus={cell.row.original.reviewStatus}
						timeSheets={cell.row.original.timeSheets}
					/>
				);
			},
		},
		{
			id: 'workSummaryReviewStatuses',
			header: 'WS Status',
			accessor: 'workSummaryReviewStatuses',
			size: 110, // 100 for "in progress" not to be cut off + padding
			cell: renderWorkSummaryStatusCell,
		},
		{
			id: 'dueDate',
			header: 'Date of Work',
			accessor: 'dueDate',
			enableSorting: true,
			enableHiding: true,
			cell: ({ cell }) => (
				<ComparableDateCell date={cell.row.original.dueDate} dateSourceFormat={TimeFormat.DB_DATE_ONLY} format={TimeFormat.DATE_ONLY} />
			),
		},
		{
			id: 'lastApprovedAt',
			header: 'Last Approved At',
			accessor: 'lastApprovedAt',
			enableSorting: true,
			enableHiding: true,
			size: 150, // typically the width needed for entire content
			cell: ({ cell }) => (
				cell.row.original.lastApprovedAt
					? (
						<DateCell
							date={cell.row.original.lastApprovedAt}
							dateSourceFormat={TimeFormat.ISO_DATETIME}
							details={cell.row.original.lastApprovedBy}
							format={TimeFormat.DATE_ONLY}
							isCustomerService={!!cell.row.original.lastApprovedAt && !cell.row.original.lastApprovedBy}
							isLeftAligned={true}
							withTime={true}
						/>
					)
					: <EmptyCell message="N/A" />
			),
		},
		{
			id: 'customerCompany',
			header: 'Customer',
			accessor: 'customerCompany',
			enableSorting: true,
			enableHiding: true,
			cell: ({ cell }) => {
				return <TextCell bold={true} value={cell.row.original.customerCompany} />;
			},
		},
		{
			id: 'title',
			header: 'Job Title',
			accessor: 'title',
			enableSorting: true,
			enableHiding: true,
			size: 130, // typically the width needed for some extra content
			cell: ({ cell }) => {
				return (
					<div className={styles['cell-ellipsed']}>
						<div className={styles['cell-ellipsed']}>{cell.row.original.jobTitle}</div>
						{cell.row.original.jobStatus &&
							<div className={`cell-additional-info ${ColorUtils.getColorTextClass(cell.row.original.jobStatusColor)}`}>
								{cell.row.original.jobStatus}
							</div>
						}
					</div>
				);
			},
		},
		{
			id: 'superintendent',
			header: 'Superintendent',
			accessor: 'superintendent',
			enableSorting: true,
			enableHiding: true,
			size: 110, // just to make sure title isn't ellipsed
			cell: ({ cell }) => {
				return <TextCell value={cell.row.original.superintendent} />;
			},
		},
		{
			id: 'projectManager',
			header: 'Project Manager',
			accessor: 'projectManager',
			enableSorting: true,
			enableHiding: true,
			size: 115, // just to make sure title isn't ellipsed
			cell: ({ cell }) => {
				return <TextCell value={cell.row.original.projectManager} />;
			},
		},
		{
			id: 'crewName',
			header: 'Crew Type',
			accessor: 'crewName',
			enableSorting: true,
			enableHiding: true,
			cell: ({ cell }) => <div className={styles['cell-ellipsed']}>
				<LabelWithColor color={cell.row.original.crewTypeColor} text={cell.row.original.crewTypeName} />
			</div>,
		},
		{
			id: 'address',
			accessor: 'address',
			header: 'Job Location',
			enableSorting: false,
			enableHiding: true,
			cell: ({ cell }) => <div className={styles['cell-ellipsed']}>
				{cell.row.original.address ? <WorkLocationAddressCell address={cell.row.original.address} /> : <EmptyCell message="N/A" />}
			</div>,
		},
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [companyData?.accountId, getRowUrlForOnClick, isAccountingOrAdmin, isManagement, renderWorkSummaryStatusCell, resolveActionsButton]);

	const buttons = React.useMemo(() => {
		const _buttons: ButtonData[] = [];

		if (process.env.FTD_DISABLE_FR_BULK_EXPORT !== 'true') {
			_buttons.push({
				type: TableButtonType.LINK,
				hasPermission: hasPermissionToViewAll,
				label: 'Bulk Export as PDF',
				icon: 'ActionDownload',
				onClick: asyncOpenMultipleJobReportModal,
			});
		}

		return _buttons;
	}, [asyncOpenMultipleJobReportModal, hasPermissionToViewAll]);

	const emptyTableMessage = React.useMemo(() => {
		if (selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB && !jobFilter) {
			return <SelectToLoad name="Job" />;
		}
		return undefined;
	}, [selectedMainFilterId, jobFilter]);

	const defaultBulkFilters: FieldReportBulkFilters = React.useMemo(() => {
		return {
			mainFilterId: selectedMainFilterId,
			searchText: tableTextFilter,
			requiresMyAttention: activeAdditionalFilters.map((item) => item.id).includes(ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION),
			assignmentFilter: assignmentFilter,
			jobFilter: jobFilter,
			projectManagerFilters: selectedProjectManagerFilters,
			superintendentFilters: selectedSuperintendentFilters,
			crewTypeFilters: selectedCrewTypeFilters,
			reviewStatusFilters: selectedReviewStatusFilters,
			workSummaryStatusFilters: selectedWorkSummaryStatusFilters,
			period: period,
			dateRange: dateRange,
			jobFilters: selectedJobFilters,
			excludeCancelled: activeAdditionalFilters.map((item) => item.id).includes(ADDITIONAL_FILTERS.EXCLUDE_CANCELLED),
		};
	}, [
		activeAdditionalFilters,
		assignmentFilter,
		dateRange,
		jobFilter,
		period,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedMainFilterId,
		selectedProjectManagerFilters,
		selectedReviewStatusFilters,
		selectedSuperintendentFilters,
		tableTextFilter,
		selectedWorkSummaryStatusFilters,
	]);

	const serializedFilters = React.useMemo(() => {
		const qp = new URLSearchParams();

		if (tableTextFilter) {
			qp.set('q', tableTextFilter);
		}
		if (selectedMainFilterId) {
			qp.set('main', selectedMainFilterId.toString());
		}
		if (selectedMainFilterId === MAIN_FILTER_OPTION_IDS.DATE) {
			if (period) {
				qp.set('period', period.toString());
			}
			if (dateRange.startDate) {
				qp.set('start', TimeUtils.formatDate(dateRange.startDate, TimeFormat.DATE_ONLY));
			}
			if (dateRange.endDate) {
				qp.set('end', TimeUtils.formatDate(dateRange.endDate, TimeFormat.DATE_ONLY));
			}
		}

		if (selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB && jobFilter !== null) {
			qp.set('jobFilter', jobFilter.toString());
		}

		if (selectedJobFilters.length) {
			qp.set('jobFilters', selectedJobFilters.map((f) => `${JSON.stringify(f)}`).join(';'));
		}
		if (selectedProjectManagerFilters.length) {
			qp.set('projectManagers', selectedProjectManagerFilters.map((f) => `${JSON.stringify(f)}`).join(';'));
		}
		if (selectedSuperintendentFilters.length) {
			qp.set('superintendents', selectedSuperintendentFilters.map((f) => `${JSON.stringify(f)}`).join(';'));
		}
		if (selectedCrewTypeFilters.length) {
			qp.set('crewTypes', selectedCrewTypeFilters.map((f) => `${JSON.stringify(f)}`).join(';'));
		}
		if (selectedReviewStatusFilters.length) {
			qp.set('reviewStatuses', selectedReviewStatusFilters.map((f) => `${JSON.stringify(f)}`).join(';'));
		}
		if (selectedWorkSummaryStatusFilters.length) {
			qp.set('workSummaryStatuses', selectedWorkSummaryStatusFilters.map((f) => `${JSON.stringify(f)}`).join(';'));
		}

		if (assignmentFilter) { qp.set('assignmentFilter', JSON.stringify(assignmentFilter)); }

		if (requiresMyAttentionFilter) { qp.set('requiresMyAttention', '1'); }

		if (excludeCancelledFilter) { qp.set('excludeCancelled', '1'); }

		return qp.toString();
	}, [
		tableTextFilter,
		selectedMainFilterId,
		jobFilter,
		selectedJobFilters,
		selectedProjectManagerFilters,
		selectedSuperintendentFilters,
		selectedCrewTypeFilters,
		selectedReviewStatusFilters,
		selectedWorkSummaryStatusFilters,
		assignmentFilter,
		requiresMyAttentionFilter,
		period,
		dateRange.startDate,
		dateRange.endDate,
		excludeCancelledFilter,
	]);

	React.useEffect(() => {
		const qs = serializedFilters ? `?${serializedFilters}` : '';
		const newUrl = `${location.pathname}${qs}`;

		if (newUrl !== window.location.pathname + window.location.search) {
			// replaceState DOES NOT cause component re-mount, which is what we want
			window.history.replaceState(null, '', newUrl);
		}
	}, [serializedFilters, location.pathname]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_JOBS_FILTER(`${companyData.id}`), `${JSON.stringify(selectedJobFilters)}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [companyData.id, selectedJobFilters]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_PROJECT_MANAGER_FILTER(`${companyData.id}`), `${JSON.stringify(selectedProjectManagerFilters)}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [companyData.id, selectedProjectManagerFilters]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_SUPERINTENDENTS_FILTER(`${companyData.id}`), `${JSON.stringify(selectedSuperintendentFilters)}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [companyData.id, selectedSuperintendentFilters]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_CREW_TYPE_FILTER(`${companyData.id}`), `${JSON.stringify(selectedCrewTypeFilters)}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [companyData.id, selectedCrewTypeFilters]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_REVIEW_STATUS_FILTER(`${companyData.id}`), `${JSON.stringify(selectedReviewStatusFilters)}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [companyData.id, selectedReviewStatusFilters]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_WORK_SUMMARY_STATUS_FILTER(`${companyData.id}`), `${JSON.stringify(selectedWorkSummaryStatusFilters)}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [companyData.id, selectedWorkSummaryStatusFilters]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_ASSIGNMENT_FILTER(`${companyData.id}`), `${JSON.stringify(assignmentFilter)}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [assignmentFilter, companyData.id]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_REQUIRES_ATTENTION_FILTER(`${companyData.id}`), `${requiresMyAttentionFilter}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [companyData.id, requiresMyAttentionFilter]);

	React.useEffect(() => {
		SettingsUtils.setItem(SettingsKeys.FIELD_REPORT_FULL_TABLE_EXCLUDE_CANCELLED_FILTER(`${companyData.id}`), `${excludeCancelledFilter}`, BrowserStorageEnum.LOCAL_STORAGE);
	}, [companyData.id, period, excludeCancelledFilter]);

	return (
		<>
			<TableNew
				additionalFilter={renderFilter}
				buttons={buttons}
				columns={columns}
				emptyTableMessage={emptyTableMessage}
				fetch={fetchData}
				getAdditionalRowClassName={getReportRowClassName}
				hasSearchInput={true}
				hasSubRows={false}
				offsetHeight={25}
				onRowClick={onRowClick}
				openBulkFilter={openBulkFilteringModal}
				pageNumberKey={PAGE_NUMBER_KEY}
				ref={tableRef}
				rowRefProp="id"
				searchTextKey={PAGE_SEARCH_TEXT_KEY}
				selectable={false}
				selectedRowKey={SELECTED_ROW_KEY}
				specializedFilters={renderSpecializedFilters}
				tableName={TableNameEnum.WORK_ORDER_FIELD_REPORT_LIST}
			/>
			<ActionSuccessModal
				{...actionSuccessModalState}
				closeModal={resetActionSuccessModalState}
			/>
			<ContactActionModal
				{...contactActionModalState}
				closeModal={resetContactActionModalState}
			/>
			<MultipleReportsActionModal
				{...multipleReportsActionModalState}
				closeModal={resetMultipleReportsActionModalState}
			/>
			<MultipleJobReportsActionModal
				closeModal={closeMultipleJobReportModal}
				initialEndDate={endDate}
				initialStartDate={startDate}
				onSubmit={onSubmitMultipleJobReports}
				showModal={showMultipleJobReportModal}
			/>
			<LoadingOverlay
				label={EXPORT_TO_FILE_LABEL}
				show={showDownloadingModal}
			/>
			<BulkFilteringModal
				applyFilters={applyBulkFilters}
				defaultValues={defaultBulkFilters}
				endDate={endDate}
				fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
				fiscalYearStartMonth={companyData.fiscalYearStartMonth}
				isOpen={isBulkFilterModalOpen}
				onClose={closeBulkFilteringModal}
				period={period}
				startDate={startDate}
			/>
			<VirtualReportExportModal
				closeModal={closeCompleteVirtualReportExportModal}
				forCustomer={false}
				onSubmit={exportCompleteVirtualReportPDF}
				showModal={showExportCompleteVirtualReportModal}
				workOrderId={exportVirtualReportModalWorkOrder?.id ?? null}
			/>
			<VirtualReportExportModal
				closeModal={closePublicVirtualReportExportModal}
				forCustomer={true}
				onSubmit={exportPublicVirtualReportPDF}
				showModal={showExportPublicVirtualReportModal}
				workOrderId={exportVirtualReportModalWorkOrder?.id ?? null}
			/>
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData, userData } = state.user;

	if (!companyData || !userData) {
		throw new Error('User not logged in');
	}

	const isAccountingOrAdmin = isAllowed(
		PagePermissions.COMPANY.FIELD_REPORT.MANAGE.ACCOUNTING,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);
	const isManagement = isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE.MANAGEMENT, companyData.permissions, companyData.isCompanyAdmin, userData.role);
	const hasPermissionToEdit = isAccountingOrAdmin
		?? isAllowed(PagePermissions.COMPANY.FIELD_REPORT.FILL, companyData.permissions, companyData.isCompanyAdmin, userData.role);
	const hasPermissionToViewAll = isAccountingOrAdmin
		?? isAllowed(PagePermissions.COMPANY.FIELD_REPORT.VIEW_ALL, companyData.permissions, companyData.isCompanyAdmin, userData.role);

	return {
		userData: userData,
		companyData,
		isAccountingOrAdmin,
		isManagement,
		hasPermissionToEdit,
		hasPermissionToViewAll,
	};
}

function mapDispatchToProps() {
	return {
		findAllForReportsTable: FieldReportActions.findAllForReportsTable,
		findReportsCSVData: WorkOrderActions.findReportsCSVData,
		findOrCreateCompleteReportBulkGroupCode: WorkOrderActions.findOrCreateCompleteGroupCode,
		downloadCompleteReportsPdf: WorkOrderActions.downloadCompleteReportPDF,
		downloadPublicReportsPdf: WorkOrderActions.downloadPublicReportPDF,
		findOrCreatePublicReportLink: WorkOrderActions.findOrCreatePublicGroupCode,
		sendCustomerSignatureRequestForReports: WorkOrderActions.sendCustomerSignatureRequestForReports,
		sendReportsForCustomer: WorkOrderActions.sendReportsForCustomer,
		findOrCreateJobReportsBulkGroupCode: WorkRequestActions.findOrCreateJobReportsBulkGroupCode,
		downloadJobReportsPdf: WorkOrderActions.downloadCompleteReportPDF,
		findOrCreateCompleteVirtualGroupCode: WorkOrderActions.findOrCreateCompleteVirtualGroupCode,
		findOrCreatePublicVirtualReportGroupCode: WorkOrderActions.findOrCreatePublicVirtualGroupCode,
		downloadCompleteVirtualReportPDF: WorkOrderActions.downloadCompleteVirtualReportPDF,
		downloadCustomerVirtualReportPDF: WorkOrderActions.downloadCustomerVirtualReportPDF,
	};
}

const normalizeDateToDate = (item: string) => {
	return TimeUtils.normalizeDateToDate(item, TimeFormat.DATE_ONLY);
};

const enhance = compose<React.ComponentClass>(
	React.memo,
	connector,
	SettingsUtils.withSettings<SettingProps>((props: Props) => {
		const companyId = `${props.companyData.id}`;
		return [
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_START_DATE(companyId),
				mappedName: 'startDate',
				normalize: normalizeDateToDate,
				defaultValue: new Date(),
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_END_DATE(companyId),
				mappedName: 'endDate',
				normalize: normalizeDateToDate,
				defaultValue: new Date(),
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_PERIOD(companyId),
				mappedName: 'period',
				defaultValue: TimePeriodRecurrence.DAILY,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_MAIN_FILTER(companyId),
				mappedName: 'mainFilter',
				normalize: (value: string) => +value,
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_JOB_ID(companyId),
				mappedName: 'jobFilterId',
				normalize: (value: string) => +value,
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_SUPERINTENDENTS_FILTER(companyId),
				mappedName: 'superintendentFilter',
				normalize: (value: string) => JSON.parse(value),
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_PROJECT_MANAGER_FILTER(companyId),
				mappedName: 'projectManagerFilter',
				normalize: (value: string) => JSON.parse(value),
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_CREW_TYPE_FILTER(companyId),
				mappedName: 'crewTypeFilter',
				normalize: (value: string) => JSON.parse(value),
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_REVIEW_STATUS_FILTER(companyId),
				mappedName: 'reviewStatusFilter',
				normalize: (value: string) => JSON.parse(value),
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_WORK_SUMMARY_STATUS_FILTER(companyId),
				mappedName: 'workSummaryStatusFilter',
				normalize: (value: string) => JSON.parse(value),
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_REQUIRES_ATTENTION_FILTER(companyId),
				mappedName: 'requiresAttentionFilter',
				normalize: (value: string) => value === 'true',
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_EXCLUDE_CANCELLED_FILTER(companyId),
				mappedName: 'excludeCancelledFilter',
				normalize: (value: string) => value === 'true',
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_ASSIGNMENT_FILTER(companyId),
				mappedName: 'assignmentFilter',
				normalize: (value: string) => JSON.parse(value),
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
			{
				key: SettingsKeys.FIELD_REPORT_FULL_TABLE_JOBS_FILTER(companyId),
				mappedName: 'jobsFilter',
				normalize: (value: string) => JSON.parse(value),
				defaultValue: null,
				source: BrowserStorageEnum.LOCAL_STORAGE,
			},
		];
	}),
	withFieldReportFullViewFilterContext,
	withTaskRecovery
);

export default enhance(WorkOrdersReportsListFullTable);
